import { Avatar } from '@nexo/ui';
import { useQuery } from '@tanstack/react-query';
import { MessageSquareQuote } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { RichText } from '../chat/RichText';

/** The chat message a task was created from, with a link to its conversation. */
export function SourceMessage({
  messageId,
  onNavigate,
}: {
  messageId: string;
  /** Called before jumping to the conversation (e.g. to close the drawer). */
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const { team } = useTeam();
  const navigate = useNavigate();
  const message = useQuery({
    queryKey: ['message', messageId],
    queryFn: () => api.messages.get(messageId),
    retry: false,
  });

  return (
    <section className="mt-6 rounded-xl border border-border bg-surface-2/60 p-4">
      <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold text-muted">
        <MessageSquareQuote className="size-4 text-accent" /> {t('Mensaje original')}
      </h3>
      {message.isPending ? (
        <p className="text-sm text-subtle">{t('Cargando…')}</p>
      ) : !message.data ? (
        <p className="text-sm text-subtle">{t('El mensaje ya no existe o no tienes acceso.')}</p>
      ) : (
        <>
          <div className="flex gap-3">
            <Avatar
              id={message.data.author.id}
              name={message.data.author.displayName}
              src={message.data.author.avatarUrl}
              size="sm"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm">
                <span className="font-semibold">{message.data.author.displayName}</span>{' '}
                <span className="text-xs text-subtle">
                  {formatRelative(message.data.createdAt)}
                </span>
              </p>
              <div className="mt-0.5 line-clamp-6 text-sm break-words whitespace-pre-line text-text">
                <RichText text={message.data.body} />
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              onNavigate?.();
              void navigate(`/app/t/${team.id}/c/${message.data.channelId}`);
            }}
            className="mt-3 text-xs font-medium text-accent hover:underline"
          >
            {t('Ver en la conversación →')}
          </button>
        </>
      )}
    </section>
  );
}
